import type { InAppNotification } from "@/modules/sys/model/types";
import { notifyEventLabel } from "@/modules/sys/model/types";

export type NotificationInboxTone = "warning" | "danger" | "info" | "neutral";

export type NotificationInboxItem = {
  id: string;
  title: string;
  summary: string;
  whenLabel: string;
  tone: NotificationInboxTone;
  isUnread: boolean;
  eventType: string;
};

const UUID_RE =
  /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i;

/** Strip legacy "Galaxy One — " title prefix (D-N2). */
export function cleanNotificationTitle(
  title: string,
  eventType: string,
): string {
  const trimmed = title.replace(/^Galaxy One\s*[—–-]\s*/i, "").trim();
  if (trimmed.length > 0) return trimmed;
  return notifyEventLabel(eventType);
}

export function looksLikeJsonPayload(body: string): boolean {
  const t = body.trim();
  return t.startsWith("{") && t.includes('"');
}

function readStr(
  obj: Record<string, unknown>,
  ...keys: string[]
): string | null {
  for (const k of keys) {
    const v = obj[k];
    if (typeof v === "string" && v.trim()) return v.trim();
    if (typeof v === "number") return String(v);
  }
  return null;
}

function formatDateVi(raw: string | null): string | null {
  if (!raw) return null;
  const d = new Date(raw);
  if (Number.isNaN(d.getTime())) return raw.length > 16 ? raw.slice(0, 16) : raw;
  return d.toLocaleDateString("vi-VN", {
    day: "2-digit",
    month: "2-digit",
    year: "numeric",
  });
}

function joinParts(...parts: Array<string | null | undefined>): string {
  return parts
    .filter((p): p is string => Boolean(p && p.trim()))
    .join(" ")
    .replace(/\s+/g, " ")
    .trim();
}

/** Compat: turn stored JSON body into a VI sentence (N2). */
export function humanizeNotificationBody(
  eventType: string,
  body: string,
): string {
  if (!looksLikeJsonPayload(body)) return body.trim();
  try {
    const obj = JSON.parse(body) as Record<string, unknown>;
    const plant = readStr(obj, "plantCode", "PlantCode");
    const lot = readStr(obj, "lotCode", "LotCode");
    const item = readStr(obj, "itemCode", "ItemCode");
    const batch = readStr(obj, "batchCode", "BatchCode");
    const orderNo = readStr(obj, "orderNo", "OrderNo");
    const reason = readStr(obj, "reason", "Reason");
    const expiry = formatDateVi(readStr(obj, "expiryDate", "ExpiryDate"));
    const plannedEnd = formatDateVi(readStr(obj, "plannedEnd", "PlannedEnd"));
    const opCode = readStr(obj, "operationCode", "OperationCode");

    switch (eventType) {
      case "CipRequired":
        return joinParts(
          plant ? `Nhà máy ${plant} — cần CIP trước khi tiếp tục.` : "Cần CIP trước khi tiếp tục.",
          batch ? `Batch ${batch}.` : null,
        );
      case "CipOverdue":
        return joinParts(
          plant ? `Nhà máy ${plant} — CIP đã quá hạn.` : "CIP đã quá hạn.",
          batch ? `Batch ${batch}.` : null,
        );
      case "ExpiryWarning":
        return joinParts(
          lot ? `Lô ${lot}` : "Lô sắp hết hạn",
          item ? `(${item})` : null,
          expiry ? `hết hạn ${expiry}.` : "sắp hết hạn.",
          plant ? `Nhà máy ${plant}.` : null,
        );
      case "ProductionDelay":
        return joinParts(
          orderNo ? `Lệnh ${orderNo}` : "Lệnh sản xuất",
          plannedEnd ? `trễ so với kế hoạch ${plannedEnd}.` : "trễ hạn.",
          plant ? `Nhà máy ${plant}.` : null,
        );
      case "LotHeld":
        return joinParts(
          lot ? `Lô ${lot}` : batch ? `Batch ${batch}` : "Lot/batch",
          "đang HOLD.",
          reason ? `Lý do: ${reason}.` : null,
          plant ? `Nhà máy ${plant}.` : null,
        );
      case "QcPending":
        return joinParts(
          "Có phiếu QC chờ xử lý.",
          opCode ? `Công đoạn ${opCode}.` : null,
          batch ? `Batch ${batch}.` : null,
          plant ? `Nhà máy ${plant}.` : null,
        );
      case "OperationReady":
        return joinParts(
          "Lệnh sẵn sàng trên floor.",
          opCode ? `Công đoạn ${opCode}.` : null,
          batch ? `Batch ${batch}.` : null,
          plant ? `Nhà máy ${plant}.` : null,
        );
      case "OperationStarted":
        return joinParts(
          "Công đoạn đã Start.",
          opCode ? `Mã ${opCode}.` : null,
          batch ? `Batch ${batch}.` : null,
          plant ? `Nhà máy ${plant}.` : null,
        );
      case "BatchCompleted":
        return joinParts(
          batch ? `Batch ${batch} hoàn tất.` : "Batch hoàn tất.",
          plant ? `Nhà máy ${plant}.` : null,
        );
      case "ApprovalPending":
        return joinParts(
          "Có phiếu chờ duyệt.",
          orderNo ? `Tham chiếu ${orderNo}.` : null,
          plant ? `Nhà máy ${plant}.` : null,
        );
      default:
        return joinParts(
          `Sự kiện ${notifyEventLabel(eventType)}.`,
          plant ? `Nhà máy ${plant}.` : null,
        );
    }
  } catch {
    return notifyEventLabel(eventType);
  }
}

export function buildNotificationSummary(
  n: InAppNotification,
  humanBody: string,
): string {
  const parts: string[] = [humanBody];
  if (n.plantCode && !humanBody.includes(n.plantCode)) {
    parts.push(n.plantCode);
  }
  // Prefer business entityId (lot/batch code) — never UUID.
  if (n.entityId && !UUID_RE.test(n.entityId) && !humanBody.includes(n.entityId)) {
    parts.push(n.entityId);
  }
  return parts.join(" · ").replace(/\s+·\s+/g, " · ").trim();
}

export function formatRelativeTimeVi(
  iso: string,
  nowMs: number = Date.now(),
  withSuffix = true,
): string {
  const t = new Date(iso).getTime();
  if (Number.isNaN(t)) return "";
  const diffSec = Math.round((nowMs - t) / 1000);
  const suffix = withSuffix ? " trước" : "";
  if (diffSec < 45) return "Vừa xong";
  if (diffSec < 3600) return `${Math.max(1, Math.floor(diffSec / 60))} phút${suffix}`;
  if (diffSec < 86400) return `${Math.floor(diffSec / 3600)} giờ${suffix}`;
  const days = Math.floor(diffSec / 86400);
  if (days === 1) return "Hôm qua";
  if (days < 7) return `${days} ngày${suffix}`;
  return new Date(iso).toLocaleDateString("vi-VN", {
    day: "2-digit",
    month: "2-digit",
  });
}

export function notificationSeverityTone(
  severity: string,
): NotificationInboxTone {
  const s = severity.toLowerCase();
  if (s === "warning") return "warning";
  if (s === "danger" || s === "error") return "danger";
  if (s === "info") return "info";
  return "neutral";
}

export function formatNotificationInboxItem(
  n: InAppNotification,
  nowMs: number = Date.now(),
): NotificationInboxItem {
  const title = cleanNotificationTitle(n.title, n.eventType);
  const humanBody = humanizeNotificationBody(n.eventType, n.body);
  return {
    id: n.id,
    title,
    summary: buildNotificationSummary(n, humanBody),
    whenLabel: formatRelativeTimeVi(n.createdAt, nowMs),
    tone: notificationSeverityTone(n.severity),
    isUnread: !n.isRead,
    eventType: n.eventType,
  };
}
